import React, { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoSite,
  SiteFeature,
} from 'types'
import { useMemoizedObject } from 'utils/hookHelpers'

type SiteViewContextType = {
  // Site & selected asset data
  site: GeckoSite | undefined
  setSite: Dispatch<SetStateAction<GeckoSite | undefined>>

  selectedAssetModel: GeckoAssetModel | undefined
  setSelectedAssetModel: Dispatch<SetStateAction<GeckoAssetModel | undefined>>

  selectedComponent: GeckoComponent | undefined
  setSelectedComponent: Dispatch<SetStateAction<GeckoComponent | undefined>>

  selectedCDF: GeckoComponentDataFrame | undefined
  setSelectedCDF: Dispatch<SetStateAction<GeckoComponentDataFrame | undefined>>
}

const SiteViewContext = React.createContext<SiteViewContextType | undefined>(
  undefined
)

const SiteViewContextProvider: React.FC<
  SiteViewContextType & {
    children: React.ReactNode
  }
> = ({ children, ...props }) => {
  const context = useMemoizedObject(props)

  return (
    <SiteViewContext.Provider value={context}>
      {children}
    </SiteViewContext.Provider>
  )
}

export const useSiteViewContext = (): SiteViewContextType => {
  const context = React.useContext(SiteViewContext)
  if (!context)
    throw new Error(
      'useSiteViewContext() must be used within a site view context'
    )
  return context
}

export const DefaultSiteViewContextProvider: FC<{
  currentSite: GeckoSite | undefined
  children: ReactNode
}> = ({ currentSite, children }) => {
  const [site, setSite] = useState<GeckoSite | undefined>(currentSite)

  const [selectedAssetModel, setSelectedAssetModel] = useState<
    GeckoAssetModel | undefined
  >(undefined)

  const [selectedComponent, setSelectedComponent] = useState<
    GeckoComponent | undefined
  >(undefined)

  const [selectedCDF, setSelectedCDF] = useState<
    GeckoComponentDataFrame | undefined
  >(undefined)

  const [selectedFeature, setSelectedFeature] = useState<
    SiteFeature | undefined
  >(undefined)

  return (
    <SiteViewContextProvider
      {...{
        site,
        setSite,
        selectedAssetModel,
        setSelectedAssetModel,
        selectedComponent,
        setSelectedComponent,
        selectedCDF,
        setSelectedCDF,
        selectedFeature,
        setSelectedFeature,
      }}>
      {children}
    </SiteViewContextProvider>
  )
}
